import React from 'react';
import { IntegrationCard as IntegrationCardType, ShelterLuvStatus } from '../../lib/types';
import { formatDate } from '../../lib/utils';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import Button from '../../components/Button/Button';
import Switch from '../../components/Switch/Switch';
import styles from './Integrations.module.scss';

const ICONS: Record<string, React.ReactNode> = {
  'import-manually': (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  shelterluv: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  'adopt-a-pet': (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  ),
};

interface IntegrationCardProps {
  integration: IntegrationCardType;
  shelterLuvStatus?: ShelterLuvStatus;
  onConnect: () => void;
  onDisconnect: () => void;
  onToggleEmailUpdates: (optIn: boolean) => void;
  emailUpdatesSaving: boolean;
}

export default function IntegrationCard({
  integration,
  shelterLuvStatus,
  onConnect,
  onDisconnect,
  onToggleEmailUpdates,
  emailUpdatesSaving,
}: IntegrationCardProps) {
  const isShelterluv = integration.id === 'shelterluv';
  const isImportManually = integration.id === 'import-manually';
  const isConnected = isShelterluv && shelterLuvStatus?.connected;
  const isPlanned = integration.status === 'planned';
  // Integrations that are either live (`import-manually`) or wired up for
  // setup (`shelterluv`) get the active styling — yellow accent + filled icon.
  const iconActive = isImportManually || isShelterluv;
  // Only render a CTA for integrations whose `onConnect` is actually wired
  // up in `Integrations.tsx`. A new id with `status: 'disconnected'` added
  // to `integrations.json` without a handler would otherwise render a
  // green 'Connect' button that no-ops on click.
  const hasConnectHandler = isImportManually || isShelterluv;

  const accentClass = isConnected ? styles.accentGreen : iconActive ? styles.accentYellow : styles.accentSubtle;

  return (
    <div className={styles.card}>
      <div className={`${styles.cardAccent} ${accentClass}`} />

      <div className={styles.cardHeader}>
        <div className={`${styles.cardIcon} ${iconActive ? styles.cardIconActive : ''}`}>{ICONS[integration.id]}</div>
        <div>
          <h3 className={styles.cardTitle}>{integration.name}</h3>
          {isConnected ? (
            <StatusBadge variant="success">Connected</StatusBadge>
          ) : isPlanned || !hasConnectHandler ? (
            <StatusBadge variant="neutral">Planned</StatusBadge>
          ) : isImportManually ? (
            <StatusBadge variant="neutral">Available</StatusBadge>
          ) : (
            <StatusBadge variant="neutral">Not Connected</StatusBadge>
          )}
        </div>
      </div>

      {isConnected && shelterLuvStatus ? (
        <>
          <div className={styles.dataGrid}>
            <div className={styles.dataItem}>
              <span className={styles.dataLabel}>Organization</span>
              <span className={styles.dataValue}>{shelterLuvStatus.orgName}</span>
            </div>
            <div className={styles.dataItem}>
              <span className={styles.dataLabel}>Chips Imported</span>
              <span className={styles.dataValue}>{shelterLuvStatus.chipsImported}</span>
            </div>
            <div className={styles.dataItem}>
              <span className={styles.dataLabel}>Last Import</span>
              <span className={styles.dataValue}>{formatDate(shelterLuvStatus.lastSync)}</span>
            </div>
          </div>
          <div className={styles.settingRow}>
            <div className={styles.settingText}>
              <span className={styles.settingLabel}>Email updates</span>
              <span className={styles.settingDescription}>
                Recieve an email each time a registration is imported from ShelterLuv
              </span>
            </div>
            <Switch
              checked={shelterLuvStatus.emailUpdateOptIn}
              onChange={onToggleEmailUpdates}
              disabled={emailUpdatesSaving}
              ariaLabel="Email updates for imported registrations"
            />
          </div>
          <Button variant="ghost" size="sm" className={styles.disconnectBtn} onClick={onDisconnect}>
            Disconnect
          </Button>
        </>
      ) : (
        <>
          <p className={styles.cardDescription}>{integration.description}</p>

          {integration.features && (
            <ul className={styles.featureList}>
              {integration.features.map((feature) => (
                <li key={feature} className={styles.featureItem}>
                  {feature}
                </li>
              ))}
            </ul>
          )}

          {isPlanned || !hasConnectHandler ? (
            <span className={styles.comingSoon}>Coming Soon</span>
          ) : (
            <Button variant="green" size="sm" onClick={onConnect}>
              {isImportManually ? 'Start Import' : 'Connect'}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
